import { TYPE_COLORS } from "@/components/cards/card-theme";
import {
  AttackRow,
  RarityDots,
  SegmentedStatBars,
  TalentBadge,
  TypePipRow,
} from "@/components/cards/designs/design-primitives";
import {
  DesignProps,
  Shell,
  Sprite,
  shade,
  useTheme,
} from "@/components/cards/designs/design-shared";

export const DesignMiniCard = ({ pokeName, level, d, fullArt, vfx }: DesignProps) => {
  const t = useTheme(d);
  const stats: [string, number, string][] = [
    ["AP", d.stats.ap, "#f8d030"],
    ["RES", d.stats.res, "#6890f0"],
    ["SPD", d.stats.spd, "#78c850"],
  ];
  return (
    <Shell t={t} fullArt={fullArt} vfx={vfx} compact style={{ background: shade(TYPE_COLORS[d.types[0]!], -0.6) }}>
      <div
        className="absolute inset-[6px] rounded-lg"
        style={{ border: `1.5px solid ${t.light}99`, background: fullArt ? `linear-gradient(180deg, ${t.main}11, ${t.dark}66)` : `linear-gradient(180deg, ${t.main}22, ${t.dark}dd)` }}
      />
      <div className="z-10 relative flex flex-col p-2.5">
        <div className="flex justify-between items-start">
          <div className="flex flex-col">
            <span className="font-black text-[12px] text-white uppercase tracking-[0.15em] leading-none">
              {pokeName}
            </span>
            <span className="font-bold text-[6px] text-white/55 uppercase tracking-[0.2em]">{d.typeLine}</span>
          </div>
          <div className="flex items-end gap-0.5">
            <span className="font-black text-[12px] text-white leading-none">{d.hp}</span>
            <span className="font-black text-[5px] text-white/60 mb-[1px]">HP</span>
          </div>
        </div>
        <div
          className="relative flex justify-center items-center mt-1.5 rounded-md h-[68px] overflow-visible"
          style={{ background: fullArt ? "transparent" : `radial-gradient(circle at 50% 45%, ${t.light}44, transparent 72%)` }}
        >
          <Sprite pokeName={pokeName} isShiny={d.isShiny} scale={1.75} />
          <div className="top-1 right-1 absolute">
            <TypePipRow types={d.types} size={18} />
          </div>
          <div className="bottom-1 left-1 absolute">
            <TalentBadge d={d} />
          </div>
        </div>
        <div className="flex gap-1 mt-1.5">
          {stats.map(([label, value, color]) => (
            <div key={label} className="flex flex-col flex-1 items-center gap-[2px] bg-black/30 rounded px-1 py-[3px]">
              <span className="font-black text-[6px] text-white/50">{label}</span>
              <span className="font-black text-[11px] text-white leading-none tabular-nums" style={{ color }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
};

export const DesignMinimal = ({ pokeName, level, d, fullArt, vfx }: DesignProps) => {
  const t = useTheme(d);
  return (
    <Shell t={t} fullArt={fullArt} vfx={vfx} style={{ background: shade(TYPE_COLORS[d.types[0]!], -0.6) }}>
      <div
        className="absolute inset-[6px] rounded-lg"
        style={{ border: `1.5px solid ${t.light}99`, background: fullArt ? `linear-gradient(180deg, ${t.main}11, ${t.dark}66)` : `linear-gradient(180deg, ${t.main}22, ${t.dark}dd)` }}
      />
      <div className="z-10 relative flex flex-col h-full p-2.5">
        <div className="flex justify-between items-start shrink-0">
          <div className="flex flex-col">
            <span className="font-black text-[12px] text-white uppercase tracking-[0.15em] leading-none">
              {pokeName}
            </span>
            <span className="font-bold text-[6px] text-white/55 uppercase tracking-[0.2em]">{d.typeLine}</span>
          </div>
          <div className="flex items-end gap-0.5">
            <span className="font-black text-[12px] text-white leading-none">{d.hp}</span>
            <span className="font-black text-[5px] text-white/60 mb-[1px]">HP</span>
          </div>
        </div>
        <div
          className="relative flex flex-1 justify-center items-center mt-1.5 rounded-md min-h-[68px] overflow-visible"
          style={{ background: fullArt ? "transparent" : `radial-gradient(circle at 50% 45%, ${t.light}44, transparent 72%)` }}
        >
          <Sprite pokeName={pokeName} isShiny={d.isShiny} scale={1.75} />
          <div className="top-1 right-1 absolute">
            <TypePipRow types={d.types} size={18} />
          </div>
          <div className="bottom-1 left-1 absolute">
            <TalentBadge d={d} />
          </div>
        </div>
        <div className="mt-1.5 shrink-0">
          <SegmentedStatBars stats={d.stats} />
        </div>
        <div className="flex flex-col gap-0.5 mt-1.5 shrink-0">
          {d.attacks.map((m) => (
            <AttackRow key={m.id} move={m} compact />
          ))}
        </div>
        <div className="flex justify-between items-center pt-1 mt-1 border-white/10 border-t shrink-0">
          <span className="font-mono text-[6px] text-white/50 tracking-widest">{d.dexId}</span>
          <RarityDots count={d.rarityDots} />
          <span className="font-mono text-[6px] text-white/50">Lv{level}</span>
        </div>
      </div>
    </Shell>
  );
};
